import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { isValidReturnUrl, isPathwaysUrl } from '../../../utils/urlUtils';
import { api } from '../../../utils/apiClient';

function appendAccessToken(url: string, token: string): string {
  const urlObj = new URL(url);
  urlObj.searchParams.set('access', token);
  return urlObj.toString();
}

/**
 * Handles redirecting authenticated users to a return URL provided in the query params.
 * This is useful when an external application (like Pathways) redirects the user to the portal
 * for authentication, and wants them redirected back after login.
 */
const ReturnUrlHandler = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasAttemptedRedirect = useRef(false);

  function appendAccessToken(url: string, token: string): string {
    const urlObj = new URL(url);
    urlObj.searchParams.set('access', token);
    return urlObj.toString();
  }

  useEffect(() => {
    if (!isAuthenticated || isLoading || !returnTo || hasAttemptedRedirect.current) return;

    if (!isValidReturnUrl(returnTo)) {
      console.warn('Invalid returnTo URL ignored:', returnTo);
      return;
    }

    const handleRedirect = async () => {
      hasAttemptedRedirect.current = true;
      setIsRedirecting(true);

      try {
        if (isPathwaysUrl(returnTo)) {
          const response = await api.createAccessTokenForProduct('pathways');
          const token = response?.data?.attributes?.accessToken;
          
          if (token) {
            console.log('Token generated. Redirecting...');
            const urlWithToken = appendAccessToken(returnTo, token);
            window.location.href = urlWithToken;
          } else {
            console.error('Failed to get access token for Pathways');
            setIsRedirecting(false);
          }
        } else {
          window.location.href = returnTo;
        }
      } catch (error) {
        console.error('Error generating access token:', error);
        setIsRedirecting(false);
      }
    };

    handleRedirect();
  }, [isAuthenticated, isLoading, returnTo]);

  if (isRedirecting) {
    return null;
  }

  return null;
};

export default ReturnUrlHandler;

